import BaseMapLayer from "../components/map/BaseMapLayer";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useTheme } from '@mui/material';
import { useAuthContext } from "../context/auth";
import { usePathContext } from "../context/path";
import { Box } from "@mui/material";
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import Alert from '@mui/material/Alert';
import { MapContainer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import "./Radar.css";

import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useTargetContext } from "../context/target";
import ZoomTracker from "../components/map/ZoomTracker";
import LocationStatusBar from "../components/map/LocationStatusBar";
import FlightMarker from "../components/map/FlightMarker";
import MapEvents from "../components/map/MapEvents";
import LinkedBorders from "../components/map/LinkedBorders";
import { linkedCountries } from "../constants/linkedCountries";
import tanzaniaGeoJSON1 from "../assets/TZA-ADM1.json";
import tanzaniaGeoJSON2 from "../assets/TZA_adm2.json";
import RadarScan from "../components/map/RadarScan";
import SettingsToggleButton from "../components/map/SettingsToggleButton";
import RadarSettingsSidebar from "../components/map/RadarSettingsSidebar";
import UnitsSidebar from "../components/map/UnitsSidebar";
import { SIDEBAR_WIDTH, UNITS_SIDEBAR_WIDTH } from "../components/map/sidebarWidths";
import CenterMap from "../components/map/CenterMap";
import AzimuthRuler from "../components/map/AzimuthRuler";
import AzimuthDistanceStatusBar from "../components/map/AzimuthDistanceStatusBar";
import socket from "../utils/socket";
import TargetsState from "../components/map/TargetsState";
import AirportMarker from "../components/map/AirportMarker";
import { decodeParams } from "../utils/cryptoUtil";
import { ROLES } from "../config/roles";
import { ShowNumberModes, SpeedUnits } from "../config/types";
import OutpostMarker from "../components/map/OutpostMarker";
import { buildIconStyle } from "../utils/mapUtils";
import GrayOverlay from "../components/map/GrayOverlay";
import NoFlyRegion from "../components/map/NoFlyRegion";
import MapSettingsSidebar from "../components/map/MapSettingsSidebar";
import MapClock from "../components/map/MapClock";
import MapUnitBadge from "../components/map/MapUnitBadge";
import RadarToolbar from "../components/map/RadarToolbar";
import { RegionLayers } from "../components/regions/RegionLayers";

const Radar = () => {
  const theme = useTheme();

  const { auth } = useAuthContext();
  const { setTitle } = usePathContext();
  const { targets, dispatch } = useTargetContext();

  const axiosPrivate = useAxiosPrivate();

  const [notFound, setNotFound] = useState(false);
  const [tick, setTick] = useState(0);
  const [scale, setScale] = useState(Math.pow(6 / 6, 1.2));
  const [zoom, setZoom] = useState(6); 
  const [position, setPosition] = useState({ lat: -6.369028, lng: 34.888822 });
  const [centerPosition, setCenterPosition] = useState([ -6.369028, 34.888822 ]);

  const [unitsSidebarOpen, setUnitsSidebarOpen] = useState(false);
  const [settingsSidebarOpen, setSettingsSidebarOpen] = useState(false);
  const [mapSettingsSidebarOpen, setMapSettingsSidebarOpen] = useState(false);
  const [radarColor, setRadarColor] = useState("3,168,168");
  const [showRadarStation, setShowRadarStation] = useState(false);
  const [showNumberMode, setShowNumberMode] = useState(ShowNumberModes.SYNTH); // 0=HIDE, 1=TRACK, 2=SYNTH
  const [speedUnit, setSpeedUnit] = useState(SpeedUnits.KNOTS); // KNOTS, KMH, MPH
  const [showBorder, setShowBorder] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [updateTimeout, setUpdateTimeout] = useState(3);
  const [trackLength, setTrackLength] = useState(20);
  const [selectedTargetId, setSelectedTargetId] = useState(null);
  const [mapMode, setMapMode] = useState('map'); // 'map' | 'merge' | 'unmerge'
  const mapRef = useRef(null);
  // Base map: 'roadmap' | 'satellite' (Google) | 'osm' (OpenStreetMap)
  const [mapType, setMapType] = useState('roadmap');

  const [radarData, setRadarData] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAirports, setShowAirports] = useState(false);
  const [showOutposts, setShowOutposts] = useState(true);
  const [showFlag, setShowFlag] = useState(true);
  const [overlayOpacity, setOverlayOpacity] = useState(0.7);  
  const [airportOpacity, setAirportOpacity] = useState(0.3);
  const [outpostOpacity, setOutpostOpacity] = useState(0.5);
  const [showNoFlyRegion, setShowNoFlyRegion] = useState(true);
  const [regionColor, setRegionColor] = useState("115, 31, 31");
  const [regionFillColor, setRegionFillColor] = useState("114, 74, 74");
  // Regions list
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [regionError, setRegionError] = useState("");

  const [enemyFilter, setEnemyFilter] = useState({
    invert: 19,
    sepia: 96,
    hue: -15,
    brightness: 95,
    contrast: 104,
    saturate: 6881,
  });

  const [friendlyFilter, setFriendlyFilter] = useState({
    invert: 42,
    sepia: 88,
    hue: 190,
    brightness: 120,
    contrast: 90,
    saturate: 520,
  });

  const [passFilter, setPassFilter] = useState({
    invert: 85,
    sepia: 47,
    hue: 3,
    brightness: 106,
    contrast: 104,
    saturate: 514,
  });

  const [unknownFilter, setUnknownFilter] = useState({
    invert: 62,
    sepia: 58,
    hue: 295,
    brightness: 102,
    contrast: 101,
    saturate: 2465,
  });

  const [importantFilter, setImportantFilter] = useState({
    invert: 19,
    sepia: 96,
    hue: -15,
    brightness: 95,
    contrast: 104,
    saturate: 6881,
  });

  const selectedUnitId = useMemo(
    () => selectedUnit?._id ?? auth?.rootUnit?._id,
    [selectedUnit, auth]
  );

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const isAdminOrRoot = auth.roles.includes(ROLES.Admin) || auth.roles.includes(ROLES.Root)
  const admin = auth && isAdminOrRoot

  const toggleFullscreen = () => {
    const mapEl = document.documentElement;
    if (!document.fullscreenElement) {
      mapEl.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  // helper to perform merge/unmerge actions
  const performMerge = async (selectedIds) => {
    // dispatch a merge action or call API here. For now, just log and dispatch a client-side action
    //console.log('Merging targets:', selectedIds);
    try {
      const res = await axiosPrivate.get("/api/targets/mergeTargets", {
          params: { selectedIds, selectedUnitId, trackLength },
        });
      dispatch({ type: 'MERGE_TARGETS', payload: res.data });
      // after merging, return to normal map mode
      setMapMode('map');
    } catch (err) {
        console.error("Failed to merge targets:", err);
        // ensure UI returns to map mode even on error
        setMapMode('map');
    }
  };

  const performUnmerge = async (targetId) => {
    //console.log('Unmerging target:', targetId);
    try {
      const res = await axiosPrivate.get("/api/targets/unmergeTarget", {
          params: { targetId, selectedUnitId, trackLength },
        });
      dispatch({ type: 'UNMERGE_TARGET', payload: res.data });
      // after merging, return to normal map mode
      setMapMode('map');
    } catch (err) {
        console.error("Failed to unmerge target:", err);
        // ensure UI returns to map mode even on error
        setMapMode('map');
    }
  };

  const performSetStatus = async (selectedIds, status) => {
    try {
      const res = await axiosPrivate.post("/api/targets/setStatusForTargets", {
        selectedIds, status, selectedUnitId, trackLength 
      });
      // replace targets with returned arranged targets
      dispatch({ type: 'SET_TARGET', payload: res.data });
    } catch (err) {
      console.error('Failed to set status for targets:', err);
    } finally {
      setMapMode('map');
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Detect online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowBorder(false); // Hide border when online
    };
    const handleOffline = () => {
      setIsOnline(false);
      setShowBorder(true); // Show border when offline
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Re-render trigger
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);  

  useEffect(() => {
    setSelectedUnit(auth?.rootUnit || null);    
    setCenterPosition(auth?.rootUnit ? [auth.rootUnit.latitude, auth.rootUnit.longitude] : [-6.369028, 34.888822]);
  }, [auth]);

  useEffect(() => {
    setTitle("Flight Management");

    let isMounted = true;

    const getAllTargets = async (selectedUnitId, trackLength, selectedTargetId) => {
      try {
        const res = await axiosPrivate.get("/api/targets/getTargets", {
          params: { selectedUnitId, trackLength, selectedTargetId },
        });
        if (isMounted) {
          // console.log("Fetched targets:", res.data);
          dispatch({ type: "SET_TARGET", payload: res.data });
          setNotFound(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Failed to fetch targets:", err);
          setNotFound(true);
        }
      }
    };

    if (auth && selectedUnitId && tick % updateTimeout === 0) {
      getAllTargets(selectedUnitId, trackLength, selectedTargetId);
    }

    return () => {
      isMounted = false; // don’t update state after unmount
    };
  }, [auth, selectedUnit, tick, dispatch, axiosPrivate, setTitle]);


  useEffect(() => {
    if (!showRadarStation || !selectedUnit || selectedUnit.type !== 3) {
      if (!showRadarStation) setRadarData(null);
      return;
    }

    const handleRadarData = (data) => {
      try {
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        const radarData = parsed;
        // const radarData = decodeParams(parsed.payload);
        setRadarData(radarData);
      } catch (err) {
        console.error("Failed to process radarData:", err);
      }
    };

    socket.on("radarData", handleRadarData);

    return () => {
      socket.off("radarData", handleRadarData);
    };
  }, [showRadarStation, selectedUnit]);

  const filteredTargets = useMemo(
    () => (targets?.length ? targets.filter((t) => t.typeName !== "UNKNOWN") : []),
    [targets]
  );

  const groupedTargets = useMemo(() => {
    const groups = {};
    filteredTargets.forEach((t) => {
      // Safely get outpostNum and TGT_NUM
      // const outpostNum =
      //   t.relatedTargets && t.relatedTargets.length > 0
      //     ? t.relatedTargets[0].outpostNum
      //     : (t.outpostNums && t.outpostNums.length > 0 ? t.outpostNums[0] : undefined);
      // const tgtNum = t.TGT_NUM ?? t.number; // fallback to t.number if TGT_NUM is missing

      // if (!tgtNum || !outpostNum) return;
      // const key = `${tgtNum}_${outpostNum}`;
      const key = t.trackId;
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return groups;
  }, [filteredTargets]);

  const groupedTargetsByOutpostNum = useMemo(() => {
    const groups = {};
    filteredTargets.forEach((t) => {
      t.outpostNums.forEach((outpostNum) => {
        if (!groups[outpostNum]) groups[outpostNum] = [];
        groups[outpostNum].push(t);
      });
    });
    return groups;
  }, [filteredTargets]);
  
  // when in unmerge mode, unmerge immediately after selecting a target
  useEffect(() => {
    if (mapMode !== "unmerge" || !selectedTargetId) return;

    const doUnmerge = async () => {
        await performUnmerge(selectedTargetId);

        setSelectedTargetId(null);
        setMapMode("map");
    };

    doUnmerge();
  }, [mapMode, selectedTargetId]);
  
  useEffect(() => {

    let isMounted = true;

    const loadRegions = async () => {
      setLoadingRegions(true);
      setRegionError("");

      try {
        const response = await axiosPrivate.get("/api/regions");
        if (isMounted) {
          setRegions(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        console.error("Error fetching regions:", err);
        if (isMounted) {
          setRegionError(err.response?.data?.error || "Failed to load regions.");
        }
      } finally {
        if (isMounted) setLoadingRegions(false);
      }
    };

    loadRegions();

    return () => {
      isMounted = false;
    };
  }, [axiosPrivate]);

  return (
    <Box m="1rem" height="89vh" display="flex" borderRadius="4px" position="relative">
      {/* Offline Alert */}
      {!isOnline && (
        <Alert 
          severity="error" 
          sx={{
            position: 'absolute',
            bottom: 32,
            right: 16,
            zIndex: 2000,
            minWidth: '300px',
          }}
        >
          ⚠️ Internet connection lost. Maps and data may not update.
        </Alert>
      )}

      {/* Map */}
      <Box 
        flex={1} 
        position="relative" 
        className={isFullscreen ? "fullscreen" : ""}
      >
        <SettingsToggleButton 
          onUnitsClick={() => {
              setUnitsSidebarOpen((o) => !o);
              setSettingsSidebarOpen(false);
              setMapSettingsSidebarOpen(false);
          }} 
          onSettingsClick={() => {
            setSettingsSidebarOpen((o) => !o);
            setUnitsSidebarOpen(false);
            setMapSettingsSidebarOpen(false);
          }} 
          onMapSettingsClick={() => {
            setMapSettingsSidebarOpen((o) => !o);
            setUnitsSidebarOpen(false);
            setSettingsSidebarOpen(false);
          }}
          isOpen={unitsSidebarOpen || settingsSidebarOpen || mapSettingsSidebarOpen}
          sidebarWidth={unitsSidebarOpen ? UNITS_SIDEBAR_WIDTH : SIDEBAR_WIDTH}
          active={unitsSidebarOpen ? 'units' : settingsSidebarOpen ? 'settings' : mapSettingsSidebarOpen ? 'map' : null}
        />

        <TargetsState
          targets={groupedTargetsByOutpostNum} 
          isOpen={unitsSidebarOpen || settingsSidebarOpen || mapSettingsSidebarOpen}
          sidebarWidth={unitsSidebarOpen ? UNITS_SIDEBAR_WIDTH : SIDEBAR_WIDTH} 
          foeFilters={{
            enemy: enemyFilter,
            friendly: friendlyFilter,
            pass: passFilter,
            unknown: unknownFilter,
          }}
        />

        {/* Clock */}
        <MapClock mode="live" time={currentTime} />

        {/* Selected unit */}
        <MapUnitBadge unit={selectedUnit} mode="live" radarView={showRadarStation && selectedUnit?.type === 3} />

        <MapContainer
          center={centerPosition}
          zoom={zoom + 0.5}     // offset by 0.5 zoom
          zoomSnap={0}          // allow fractional zoom
          zoomDelta={0.25}      // optional: makes scroll smoother
          style={{ height: "100%", width: "100%" }}
          whenCreated={(mapInstance) => { mapRef.current = mapInstance; }}
        >
          <CenterMap center={centerPosition} zoom={zoom + 0.5} />  {/* moves and zooms together; zoom offset by 0.5 */}
          
          <ZoomTracker setScale={setScale} />

          {/* Fullscreen Toggle Button */}
          <Box
            position="absolute"
            top="86px"
            left="12px"
            zIndex={1000}
          >
            <IconButton
              onClick={toggleFullscreen}
              size="small"
              sx={{
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
              }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Box>

          <BaseMapLayer type={mapType} />

          { showAirports && (
            <>
              <AirportMarker 
                center={{ lat: centerPosition[0], lng: centerPosition[1] }}
                scale={scale} 
                opacity={airportOpacity}/>
            </>
          )}

          { showOutposts && (
            <OutpostMarker opacity={outpostOpacity} />
          )}

          <GrayOverlay overlayOpacity={overlayOpacity} />
          
          { showBorder && (
            <>
              {/* have to draw background sea with #AAD3DF */}
              <LinkedBorders countries={linkedCountries} />

              <GeoJSON
                data={tanzaniaGeoJSON1}
                style={{
                  color: "#FFD700",
                  weight: 0.3,
                  fillOpacity: 0.05,
                  fillColor: "#A2E4D7",
                }}
              />
            </>
          )}          

          { showNoFlyRegion && (
            <RegionLayers
              regions={regions}
              selectedId={null}
              color={`rgb(${regionColor})`}
              fillColor={`rgb(${regionFillColor})`}
            />
          )}          

          { showRadarStation && selectedUnit && selectedUnit.type === 3 && (          
            <>
              <AzimuthRuler
                center={centerPosition}
                radius={500000}
                step={10}
                ringInterval={50000}
                beamWidth={1}
                rotationSpeed={1.2}
                threatLevel={"normal"}
                color={radarColor}
              />

              <RadarScan
                center={{ lat: centerPosition[0], lng: centerPosition[1] }}
                radiusKm={500}
                beamAngle={1}
                rotationSpeed={1.2}
                trailSteps={15}
                color={radarColor}
                ringCount={Math.floor(500000 / 50000)}
                radarData={radarData}
                showNoData={true}
              />
            </>
          )}

          <FlightMarker 
            groupedTargets={groupedTargets} 
            scale={scale} 
            showNumberMode={showNumberMode} 
            speedUnit={speedUnit}
            currentTime={Date.now()} 
            selectedTargetId={selectedTargetId}
            setSelectedTargetId={setSelectedTargetId}
            showFlag={showFlag}
            foeFilters={{
              enemy: enemyFilter,
              friendly: friendlyFilter,
              pass: passFilter,
              unknown: unknownFilter,
            }}
          />
          
          <MapEvents 
            setPosition={setPosition} 
            mapMode={mapMode} 
            groupedTargets={groupedTargets} 
            onMerge={performMerge} 
            onSetStatus={performSetStatus} 
          />

        </MapContainer>

        {admin && (
          <RadarToolbar
            mapMode={mapMode}
            onModeChange={(value) => {
              setMapMode(value);
              setSelectedTargetId(null);
            }}
            filters={{
              enemy: enemyFilter,
              friendly: friendlyFilter,
              pass: passFilter,
              unknown: unknownFilter,
              important: importantFilter,
            }}
            buildIconStyle={buildIconStyle}
          />
        )}

        { showRadarStation && selectedUnit && selectedUnit.type === 3 && (          
          <AzimuthDistanceStatusBar from={selectedUnit} to={position} />
        )}

        <LocationStatusBar position={position} />

        {/* when in unmerge mode, unmerge immediately after selecting a target (handled in component body) */}

        {unitsSidebarOpen && (
          <UnitsSidebar
            selectedUnit={selectedUnit}
            setSelectedUnit={setSelectedUnit}
            setCenterPosition={setCenterPosition}
            showRadarStation={showRadarStation}
            setShowRadarStation={setShowRadarStation}
            zoom={zoom}
            setZoom={setZoom}
            
          />          
        )}
        {settingsSidebarOpen && (
          <RadarSettingsSidebar
            selectedUnit={selectedUnit}
            setSelectedUnit={setSelectedUnit}            
            showNumberMode={showNumberMode}
            setShowNumberMode={setShowNumberMode}
            speedUnit={speedUnit}
            setSpeedUnit={setSpeedUnit}
            radarColor={radarColor}
            setRadarColor={setRadarColor}            
            updateTimeout={updateTimeout}
            setUpdateTimeout={setUpdateTimeout}
            trackLength={trackLength}
            setTrackLength={setTrackLength}            
            showFlag={showFlag}
            setShowFlag={setShowFlag}            
            enemyFilter={enemyFilter}
            setEnemyFilter={setEnemyFilter}
            friendlyFilter={friendlyFilter}
            setFriendlyFilter={setFriendlyFilter}
            passFilter={passFilter}
            setPassFilter={setPassFilter}
            unknownFilter={unknownFilter}
            setUnknownFilter={setUnknownFilter}
          />
        )}

        {mapSettingsSidebarOpen && (
          <MapSettingsSidebar
            mapType={mapType}
            setMapType={setMapType}
            showBorder={showBorder}
            setShowBorder={setShowBorder}
            showAirports={showAirports}
            setShowAirports={setShowAirports}
            showOutposts={showOutposts}
            setShowOutposts={setShowOutposts}
            outpostOpacity={outpostOpacity}
            setOutpostOpacity={setOutpostOpacity}
            overlayOpacity={overlayOpacity}
            setOverlayOpacity={setOverlayOpacity}
            showNoFlyRegion={showNoFlyRegion}
            setShowNoFlyRegion={setShowNoFlyRegion}
            noFlyRegionColor={regionColor}
            setNoFlyRegionColor={setRegionColor}
            noFlyRegionFillColor={regionFillColor}
            setNoFlyRegionFillColor={setRegionFillColor}
            airportOpacity={airportOpacity}
            setAirportOpacity={setAirportOpacity}
          />
        )}
      </Box>
    </Box>
  );
};

export default Radar;